import { Link, Outlet, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { useSession, useSignOutMutation } from '@/entities/session'
import {
  addMonths,
  cn,
  fmtMonthTitle,
  fromISODate,
  isSameDay,
  isToday,
  monthGrid,
  startOfDay,
  toISODate,
} from '@/shared/lib'
import { Icon, type IconName } from '@/shared/ui/icon'
import { IconButton } from '@/shared/ui/icon-button'

// adminOnly — раздел, который сотруднику не показывается: за ним всё равно
// стоит отказ бэкенда, а пункт меню, ведущий в стену, хуже отсутствующего.
const NAV: { to: string; label: string; icon: IconName; adminOnly?: true }[] = [
  { to: '/schedule', label: 'Расписание', icon: 'calendar' },
  { to: '/tasks', label: 'Дела', icon: 'check' },
  { to: '/excursions', label: 'Экскурсии', icon: 'pin', adminOnly: true },
  { to: '/clients', label: 'Клиенты', icon: 'users' },
  { to: '/team', label: 'Команда', icon: 'user', adminOnly: true },
  { to: '/settings', label: 'Настройки', icon: 'settings' },
]

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']

/**
 * Оболочка кабинета: сайдбар с мини-календарём месяца сверху и разделами под
 * ним. На телефоне сайдбар уезжает в drawer — кабинет открывают в поле.
 */
export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { session } = useSession()
  const nav = NAV.filter((item) => !item.adminOnly || session?.isAdmin)
  // На телефоне разделы спрятаны в drawer, и единственная подпись «voron» не
  // отвечала, где человек сейчас стоит.
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const current = nav.find((item) => pathname.startsWith(item.to))

  return (
    <div className="flex h-full min-h-0">
      <aside
        aria-label="Разделы кабинета"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col gap-4 border-r bg-bg-base px-3 pt-safe-6 pb-4 transition-transform md:static md:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <MiniCalendar onPick={() => setDrawerOpen(false)} />
        <nav className="flex flex-col gap-0.5">
          {nav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setDrawerOpen(false)}
              activeProps={{ className: 'bg-bg-surface text-text-primary' }}
              className="flex items-center gap-2.5 rounded-control px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
            >
              <Icon name={item.icon} size={17} />
              {item.label}
            </Link>
          ))}
        </nav>
        <SessionFooter />
      </aside>

      {drawerOpen && (
        <button
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-30 bg-black/60 md:hidden"
        />
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b px-3 pt-safe-2 pb-2 md:hidden">
          <IconButton icon="menu" label="Меню" onClick={() => setDrawerOpen(true)} />
          <span className="min-w-0 truncate text-sm text-text-primary">
            {current?.label ?? 'voron'}
          </span>
        </header>
        <main className="flex min-h-0 flex-1 flex-col px-safe-0">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function SessionFooter() {
  const { session } = useSession()
  const signOut = useSignOutMutation()
  const navigate = useNavigate()

  return (
    <div className="mt-auto flex items-center justify-between gap-2 pb-safe-0">
      <span className="min-w-0 truncate text-xs text-text-tertiary">{session?.name}</span>
      <button
        type="button"
        aria-label="Выйти"
        onClick={() =>
          signOut.mutate(undefined, { onSuccess: () => void navigate({ to: '/login' }) })
        }
        className="text-text-tertiary transition-colors hover:text-text-primary"
      >
        <Icon name="logout" size={17} />
      </button>
    </div>
  )
}

/**
 * Мини-календарь месяца: точка входа в любой день расписания и указатель, где
 * это расписание сейчас стоит. Красная плашка держится за открытый день, а не
 * за сегодня: она отвечает на «какое число я смотрю», и сидя на месте
 * обманывала бы. Сегодня остаётся помеченным цветом цифры.
 */
function MiniCalendar({ onPick }: { onPick: () => void }) {
  // Дата расписания живёт в URL. Мини-календарь висит в общей оболочке и про
  // роут не знает — отсюда нестрогое чтение search: на других разделах поля
  // просто нет, и календарь стоит на сегодня.
  const search: { date?: string } = useSearch({ strict: false })
  const selected = (search.date ? fromISODate(search.date) : null) ?? startOfDay(new Date())
  const selectedKey = toISODate(selected)

  const [anchor, setAnchor] = useState(selected)
  // Листание месяцев — своё состояние, но выбор дня снаружи (стрелки и «Сегодня»
  // в расписании) перевешивает: календарь идёт за расписанием, а не наоборот.
  const [followed, setFollowed] = useState(selectedKey)
  if (followed !== selectedKey) {
    setFollowed(selectedKey)
    setAnchor(selected)
  }

  const cells = monthGrid(anchor)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="Предыдущий месяц"
          onClick={() => setAnchor(addMonths(anchor, -1))}
          className="text-text-tertiary hover:text-text-primary"
        >
          <Icon name="chevron-left" size={16} />
        </button>
        <span className="text-sm text-text-primary">{fmtMonthTitle(anchor)}</span>
        <button
          type="button"
          aria-label="Следующий месяц"
          onClick={() => setAnchor(addMonths(anchor, 1))}
          className="text-text-tertiary hover:text-text-primary"
        >
          <Icon name="chevron-right" size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {WEEKDAYS.map((label) => (
          <span key={label} className="text-center text-[10px] text-text-tertiary">
            {label}
          </span>
        ))}
        {cells.map((cell) => {
          const picked = isSameDay(cell.date, selected)
          return (
            <Link
              key={toISODate(cell.date)}
              to="/schedule"
              search={{ date: toISODate(cell.date) }}
              onClick={onPick}
              className={cn(
                'mono-num rounded-control py-1 text-center text-xs transition-colors',
                // Приглушение прозрачностью уводит контраст ниже 4.5:1 —
                // соседний месяц отличаем тоном из палитры, а не opacity.
                cell.outside ? 'text-text-tertiary' : 'text-text-primary',
                isToday(cell.date) && !picked && 'text-accent',
                picked ? 'bg-accent text-text-inverse' : 'hover:bg-bg-elevated',
              )}
            >
              {cell.date.getDate()}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
